import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import api from '../../services/api';

const STATUS_OPTIONS = [
  { label: 'Todas', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Partially approved', value: 'partially_approved' },
  { label: 'Rejected', value: 'rejected' },
];

const AUTHORIZATION_OPTIONS = [
  { label: 'Todas', value: 'all' },
  { label: 'Manual', value: 'manual' },
  { label: 'Auto por sistema', value: 'auto' },
];

function statusSeverity(status) {
  if (status === 'approved') return 'success';
  if (status === 'partially_approved') return 'info';
  if (status === 'rejected') return 'danger';
  return 'warning';
}

function resolveAuthorizationType(row) {
  if (row.authorizationType === 'auto' || row.authorizationType === 'manual') {
    return row.authorizationType;
  }
  if (row.moderation?.reviewedBy === 'auto-authorizer') return 'auto';
  if (row.moderation?.reviewedBy) return 'manual';
  return null;
}

function authorizationLabel(type) {
  if (type === 'auto') return 'Auto por sistema';
  if (type === 'manual') return 'Manual';
  return 'Pendiente';
}

export default function CommunityModerationList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('pending');
  const [authorizationType, setAuthorizationType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [todayRules, setTodayRules] = useState(null);
  const toast = useRef(null);
  const navigate = useNavigate();

  const fetchReports = async (nextStatus = status, nextAuthorization = authorizationType) => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (nextStatus && nextStatus !== 'all') params.status = nextStatus;
      if (nextAuthorization && nextAuthorization !== 'all') {
        params.authorizationType = nextAuthorization;
      }
      const { data } = await api.get('/community_reports', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load reports' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports('pending');
    (async () => {
      try {
        const { data } = await api.get('/auto_authorize_rules');
        setTodayRules(data.today || null);
      } catch {
        setTodayRules(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    setStatus('pending');
    setAuthorizationType('all');
    fetchReports('pending', 'all');
  };

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>Community moderation</span>
              <Badge
                className="ml-2 b-4 relative"
                value={total ?? 0}
                style={{ bottom: '3px', backgroundColor: '#2020A0' }}
              />
            </h2>
          </div>
          <div className="col-6 flex justify-content-end">
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              className="p-button-primary p-button-sm secondary-button"
              onClick={() => fetchReports()}
            />
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 flex gap-2 align-items-end flex-wrap">
            <span style={{ minWidth: 200 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Status</strong>
              </label>
              <Dropdown
                value={status}
                options={STATUS_OPTIONS}
                onChange={(e) => {
                  const next = e.value || 'all';
                  setStatus(next);
                  fetchReports(next, authorizationType);
                }}
                className="w-full"
              />
            </span>
            <span style={{ minWidth: 220 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Authorization</strong>
              </label>
              <Dropdown
                value={authorizationType}
                options={AUTHORIZATION_OPTIONS}
                onChange={(e) => {
                  const next = e.value || 'all';
                  setAuthorizationType(next);
                  fetchReports(status, next);
                }}
                className="w-full"
              />
            </span>
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-text text-danger p-button-sm"
              onClick={clearFilters}
            />
          </div>
        </div>

        {todayRules && (
          <div
            className="mb-4 p-3 border-round"
            style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
          >
            <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <strong className="main-text">Auto autorizador hoy · {todayRules.label}</strong>
                {!todayRules.enabled ? (
                  <p className="admin-page-hint mt-2">Pausado. Todo va a revisión manual.</p>
                ) : todayRules.autoLabels?.length ? (
                  <p className="admin-page-hint mt-2">
                    Auto: {todayRules.autoLabels.join(', ')}. Manual:{' '}
                    {todayRules.manualLabels?.length ? todayRules.manualLabels.join(', ') : 'ninguna'}.
                  </p>
                ) : (
                  <p className="admin-page-hint mt-2">
                    Ninguna sección auto. Todo pasa por moderación.
                  </p>
                )}
              </div>
              <Link to="/auto_authorize_rules" className="p-button p-button-text p-button-sm">
                Edit rules
              </Link>
            </div>
          </div>
        )}

        {loading && !items.length ? (
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading reports...
            </div>
          </div>
        ) : (
          <DataTable
            value={items}
            paginator
            rows={20}
            loading={loading}
            emptyMessage="No reports found."
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              header="Status"
              body={(row) => (
                <Tag value={row.status} severity={statusSeverity(row.status)} />
              )}
            />
            <Column
              header="Authorization"
              body={(row) => {
                const type = resolveAuthorizationType(row);
                return (
                  <Tag
                    value={authorizationLabel(type)}
                    severity={type === 'auto' ? 'info' : type === 'manual' ? 'success' : 'warning'}
                  />
                );
              }}
            />
            <Column
              header="User"
              body={(row) => row.userId?.displayName || row.userName || '—'}
            />
            <Column field="crossingName" header="Crossing" />
            <Column field="laneType" header="Lane" />
            <Column field="transportMode" header="Mode" />
            <Column
              header="Sections"
              body={(row) =>
                [
                  row.text ? 'text' : null,
                  row.photoUrl ? 'photo' : null,
                  row.lineStartLocation ? 'line' : null,
                  row.waitBucketMinutes != null ? 'wait' : null,
                ]
                  .filter(Boolean)
                  .join(', ') || '—'
              }
            />
            <Column field="pointsAwarded" header="Pts" />
            <Column
              header="Created"
              body={(row) => row.createdAt && new Date(row.createdAt).toLocaleString()}
            />
            <Column
              header="Review"
              body={(row) => (
                <Button
                  icon="pi pi-check-square"
                  className="p-button-text p-button-sm"
                  onClick={() => navigate(`/community_moderation/${row._id}`)}
                  tooltip="Review report"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
              style={{ width: '6rem', textAlign: 'center' }}
            />
          </DataTable>
        )}
      </div>
    </Card>
  );
}
